import { forwardRef, useEffect, type HTMLAttributes, type ReactNode } from 'react';

export interface ModalProps extends HTMLAttributes<HTMLDivElement> {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  footer?: ReactNode;
}

const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ children, isOpen, onClose, title, size = 'md', showCloseButton = true, closeOnBackdropClick = true, footer, className = '', ...props }, ref) => {
    const sizeClasses = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg', xl: 'max-w-xl' };

    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) onClose(); };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    useEffect(() => {
      document.body.style.overflow = isOpen ? 'hidden' : '';
      return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-md animate-fade-in" onClick={closeOnBackdropClick ? onClose : undefined} />
        <div ref={ref} className={`relative w-full ${sizeClasses[size]} rounded-2xl bg-dark-surface border border-white/5 shadow-card-lg animate-slide-up ${className}`} {...props}>
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              {title && <h2 className="text-lg font-semibold text-white">{title}</h2>}
              {showCloseButton && (
                <button onClick={onClose} className="p-1 text-text-muted hover:text-white transition-colors rounded-lg hover:bg-white/5" aria-label="Close modal">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}
          <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">{children}</div>
          {footer && <div className="px-6 py-4 border-t border-white/5 flex items-center justify-end gap-3">{footer}</div>}
        </div>
      </div>
    );
  }
);

Modal.displayName = 'Modal';
export default Modal;
